import { config } from '../../config';
import { apiV2 } from '../lib/baseAxios';
import { getCurrentOutputLanguageName } from '../hooks/useOutputLanguage';
import {
  HIGH_QUALITY_AUDIO_BITRATE,
  LOW_QUALITY_AUDIO_BITRATE,
} from '../constants/mediaFormatProfiles';
import { getToken } from '../lib/localStorageHandler';
import { setAudioBitrate } from '../recorder/recorderSlice';
import { AppDispatch } from '../recorder/store';
import { getCustomLogger } from '../lib/logger';
import AgentDictationService from './agentDictationService';

const logger = getCustomLogger('DictationService');

/**
 * Dictation transcription provider configuration.
 *
 * Available providers:
 * - 'default': Fireworks/Gemini via unified WebSocket (current production)
 * - 'livekit': LiveKit agent with real-time transcription (experimental)
 *
 * Configure via VITE_DICTATION_PROVIDER environment variable.
 */
const DICTATION_PROVIDER: 'default' | 'livekit' = config.dictationProvider;

interface DictationItem {
  id: string;
  title: string;
  text: string;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
}

interface CreateDictationPayload {
  title: string;
  text: string;
}

interface UpdateDictationPayload {
  title?: string;
  text?: string;
}

interface StreamingTranscriptionCallbacks {
  onTranscriptionUpdate: (text: string) => void;
  onComplete: (finalText: string) => void;
  onError: (error: string) => void;
}

interface TranscribeStreamArgs {
  audioGenerator: AsyncGenerator<Uint8Array, void, unknown>;
  apiKey?: string;
  prompt?: string;
  response_format?: string;
  temperature?: number;
  language?: string;
  clientConfig?: Record<string, unknown>;
  abortSignal?: AbortSignal;
}

type ApiResponse = { text: string } | string | null;

class DictationService {
  private static readonly MIN_TRANSCRIPT_LENGTH = 3;

  private static readonly ARTIFACTS = ['thank you.', 'thank you'];

  /**
   * Audio generator for real-time microphone input (adapted from LiveTranscriber)
   */
  // eslint-disable-next-line @typescript-eslint/require-await
  static async *createMicrophoneAudioGenerator(
    abortSignal: AbortSignal,
  ): AsyncGenerator<Uint8Array, void, unknown> {
    let stream: MediaStream | null = null;
    let audioContext: AudioContext | null = null;
    let processor: ScriptProcessorNode | null = null;
    let source: MediaStreamAudioSourceNode | null = null;

    const chunkQueue: Uint8Array[] = [];
    let isProcessing = true;

    try {
      // Get microphone access
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      // Create AudioContext for real-time processing
      const AudioContextClass =
        window.AudioContext || (window as any).webkitAudioContext;
      audioContext = new AudioContextClass({
        sampleRate: 16000,
      });

      source = audioContext.createMediaStreamSource(stream);

      // Use ScriptProcessorNode for real-time audio processing
      processor = audioContext.createScriptProcessor(4096, 1, 1);

      processor.onaudioprocess = (event) => {
        if (abortSignal.aborted) return;

        const { inputBuffer } = event;
        const inputData = inputBuffer.getChannelData(0);

        // Convert Float32Array to Int16Array (PCM 16-bit)
        const { length } = inputData;
        const result = new Int16Array(length);
        for (let i = 0; i < length; i += 1) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          result[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }

        // Convert to Uint8Array and queue it
        const uint8Array = new Uint8Array(result.buffer);
        chunkQueue.push(uint8Array);
      };

      source.connect(processor);
      processor.connect(audioContext.destination);

      // Yield chunks as they become available
      // eslint-disable-next-line no-await-in-loop
      while (isProcessing && !abortSignal.aborted) {
        if (chunkQueue.length > 0) {
          const chunk = chunkQueue.shift()!;
          yield chunk;
        } else {
          // Wait a bit before checking again
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
      }
    } finally {
      // Cleanup
      isProcessing = false;
      if (processor) {
        processor.disconnect();
      }
      if (source) {
        source.disconnect();
      }
      if (audioContext) {
        audioContext.close();
      }
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    }
  }

  /**
   * Streaming transcription function using unified SASR endpoint
   * Supports multiple providers (fireworks, fireworks-v2, gemini)
   * Uses full mode by default for dictation (sends complete transcript each update)
   */
  // eslint-disable-next-line @typescript-eslint/require-await
  static async *transcribeStreamAsync({
    audioGenerator,
    prompt,
    response_format: responseFormat, // Kept for backwards compatibility but not used
    temperature,
    language,
    abortSignal,
  }: TranscribeStreamArgs): AsyncGenerator<ApiResponse, void, unknown> {
    // Use unified SASR endpoint with full mode (best for dictation)
    const wsUrl = `${config.authBaseUrlV2
      .replace(/^http:/, 'ws:')
      .replace(/^https:/, 'wss:')}/proxy/sasr`;

    const queryParams = new URLSearchParams();

    // Use Silero VAD v5 + Groq Whisper for local voice activity detection with transcription
    // Eliminates need for external LiveKit infrastructure
    queryParams.append('provider', 'silero5-groq');

    // Full mode: sends complete transcript with all segments each time
    // Better for dictation where you want the full context
    queryParams.append('mode', 'full');

    if (prompt) {
      queryParams.append('prompt', prompt);
    }
    if (temperature !== undefined) {
      queryParams.append('temperature', temperature.toString());
    }
    if (language) {
      queryParams.append('language', language);
    }

    // The final WebSocket URL - unified SASR endpoint
    const finalWsUrl = `${wsUrl}?${queryParams.toString()}`;

    const messageQueue: Array<string> = [];
    let isDone = false;

    const ws = new WebSocket(finalWsUrl);

    // Wait for the WebSocket to open
    await new Promise<void>((resolve, reject) => {
      ws.onopen = () => {
        logger.info('Connected to WebSocket:', finalWsUrl);
        resolve();
      };

      ws.onerror = (error) => {
        logger.error('WebSocket error:', error);
        reject(new Error('WebSocket connection failed'));
      };
    });

    ws.onmessage = async (event) => {
      const { data } = event;
      let decodedMessage: string;

      if (typeof data === 'string') {
        decodedMessage = data;
      } else if (data instanceof ArrayBuffer) {
        decodedMessage = new TextDecoder().decode(data);
      } else if (data instanceof Blob) {
        // Handle Blob data type
        decodedMessage = await data.text();
      } else {
        logger.error('Unsupported data type received:', typeof data, data);
        return;
      }

      // Push the decoded message into the queue
      messageQueue.push(decodedMessage);
    };

    ws.onerror = (error) => {
      logger.error('WebSocket error:', error);
      throw new Error('WebSocket error');
    };

    ws.onclose = () => {
      isDone = true;
    };

    // Send audio chunks in background
    (async () => {
      try {
        // eslint-disable-next-line no-restricted-syntax
        for await (const audioChunk of audioGenerator) {
          if (abortSignal?.aborted) {
            ws.close();
            break;
          }
          ws.send(audioChunk);
        }
        ws.close();
      } catch (error) {
        ws.close();
        logger.error('Error sending audio chunks:', error);
      }
    })();

    // Now yield messages as they come in
    // eslint-disable-next-line no-await-in-loop
    while (!isDone || messageQueue.length > 0) {
      while (messageQueue.length > 0) {
        const message = messageQueue.shift()!;
        let parsed: any;
        try {
          parsed = JSON.parse(message);
        } catch {
          // If parse fails, yield as plain text
          yield message;
          // eslint-disable-next-line no-continue
          continue;
        }

        if (parsed.error) {
          throw new Error(parsed.error);
        }

        // If response_format is 'verbose_json', yield as JSON
        // otherwise, treat as plain text
        if (!responseFormat || responseFormat === 'verbose_json') {
          yield parsed;
        } else {
          yield message; // plain-text fallback
        }
      }
      // Avoid busy looping
      // eslint-disable-next-line no-await-in-loop
      await new Promise((res) => setTimeout(res, 100));
    }
  }

  /**
   * Process streaming audio with real-time transcription
   */
  static async processStreamingAudioDictation(
    abortController: AbortController,
    callbacks: StreamingTranscriptionCallbacks,
    dispatch: AppDispatch,
    options: {
      languageCode?: string;
      customVocabulary?: string;
      windowContext?: Record<string, any>;
      mode?: 'fast' | 'best' | 'answer';
      outputLanguage?: string;
    } = {},
  ): Promise<void> {
    // Use LiveKit agent dictation if configured
    if (DICTATION_PROVIDER === 'livekit') {
      await AgentDictationService.processStreamingAudioDictation(
        abortController,
        callbacks,
        dispatch,
        options,
      );
      return;
    }

    const {
      languageCode,
      customVocabulary,
      windowContext,
      mode = 'fast',
      outputLanguage,
    } = options;

    try {
      await dispatch(setAudioBitrate(LOW_QUALITY_AUDIO_BITRATE));
      logger.info('🎯 Starting streaming dictation processing');

      const targetLanguage = await getCurrentOutputLanguageName();
      const systemLanguageCode = navigator.language.split('-')[0];

      // Create audio generator
      const audioGenerator = this.createMicrophoneAudioGenerator(
        abortController.signal,
      );

      // Start streaming transcription with unified SASR endpoint
      const streamArgs: TranscribeStreamArgs = {
        audioGenerator,
        language: languageCode || systemLanguageCode,
        // response_format is no longer needed - unified endpoint always returns JSON
        abortSignal: abortController.signal,
      };

      let accumulatedTranscript = '';
      let lastProcessedTranscript = '';

      // Process transcription stream
      try {
        // eslint-disable-next-line no-restricted-syntax
        for await (const transcriptPart of this.transcribeStreamAsync(
          streamArgs,
        )) {
          if (abortController.signal.aborted) break;

          let newText = '';

          if (typeof transcriptPart === 'string') {
            newText = transcriptPart;
          } else if (transcriptPart && typeof transcriptPart === 'object') {
            // Handle verbose response format
            if ('text' in transcriptPart) {
              newText = transcriptPart.text;
            }

            // If we have segments, use them for more accurate transcription
            if (
              'segments' in transcriptPart &&
              Array.isArray(transcriptPart.segments)
            ) {
              const segmentTexts = transcriptPart.segments
                .map((segment: any) => segment.text)
                .join(' ');
              if (segmentTexts.trim()) {
                newText = segmentTexts;
              }
            }
          }

          if (newText.trim() && newText.trim() !== lastProcessedTranscript) {
            accumulatedTranscript = newText.trim();
            lastProcessedTranscript = accumulatedTranscript;
            callbacks.onTranscriptionUpdate(accumulatedTranscript);
          }
        }
      } catch (error) {
        logger.error('Streaming transcription error:', error);
        callbacks.onError(
          error instanceof Error ? error.message : 'Transcription failed',
        );
        return;
      }

      // Process the final transcript if we have any
      if (accumulatedTranscript.trim()) {
        // Apply the same filtering as the original implementation
        const trimmedTranscript = accumulatedTranscript.trim().toLowerCase();
        if (
          accumulatedTranscript.length < this.MIN_TRANSCRIPT_LENGTH ||
          this.ARTIFACTS.includes(trimmedTranscript)
        ) {
          logger.warn('⚠️ Final transcript too short or contains artifacts');
          callbacks.onComplete('');
          return;
        }

        // Always process with LLM using process-text endpoint (this is CRITICAL!)
        let finalTranscript = this.removeMDashes(accumulatedTranscript);

        try {
          finalTranscript = await this.processWithLLM(finalTranscript, {
            mode,
            customVocabulary,
            windowContext,
            outputLanguage: outputLanguage || targetLanguage,
            languageCode: languageCode || systemLanguageCode,
          });
        } catch (error) {
          logger.error('LLM processing failed, using raw transcript:', error);
          // Continue with raw transcript if LLM processing fails
        }

        callbacks.onComplete(finalTranscript);
      } else {
        callbacks.onComplete('');
      }
    } catch (error) {
      logger.error(
        '❌ Error in streaming dictation processing, falling back to groq method:',
        error,
      );

      // Fallback to old groq method if websocket fails
      try {
        logger.info('🔄 Falling back to groq audio processing method');

        // We need to create a temporary audio blob for the fallback
        // This is a simplified fallback - in a real scenario you'd need to capture the audio differently
        callbacks.onError('Websocket failed, please try again');
        return;
      } catch (fallbackError) {
        logger.error('❌ Fallback method also failed:', fallbackError);
        callbacks.onError('Both streaming and fallback methods failed');
      }
    } finally {
      dispatch(setAudioBitrate(HIGH_QUALITY_AUDIO_BITRATE));
    }
  }

  /**
   * Process transcript with LLM (extracted from original implementation)
   */
  private static async processWithLLM(
    transcript: string,
    options: {
      mode: 'fast' | 'best' | 'answer';
      customVocabulary?: string;
      windowContext?: Record<string, any>;
      outputLanguage: string;
      languageCode: string;
    },
  ): Promise<string> {
    const {
      mode,
      customVocabulary,
      windowContext,
      outputLanguage,
      languageCode,
    } = options;

    const queryParams = new URLSearchParams({
      mode,
      language: languageCode,
      targetLanguage: outputLanguage,
      skipLLMProcessing: 'false',
    });

    if (customVocabulary) {
      queryParams.append('customVocabulary', customVocabulary);
    }
    if (windowContext) {
      queryParams.append('windowContext', JSON.stringify(windowContext));
    }

    const token = getToken();

    // Send just the transcript text for LLM processing
    const response = await fetch(
      `${
        config.authBaseUrlV2
      }/dictations/process-text?${queryParams.toString()}`,
      {
        method: 'POST',
        body: transcript,
        headers: {
          'Content-Type': 'text/plain',
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to process transcript with LLM: ${response.status} - ${errorText}`,
      );
    }

    if (!response.body) {
      throw new Error('Response body is empty');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let processedTranscript = '';

    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split('\n');

      // eslint-disable-next-line no-loop-func
      lines.forEach((line) => {
        if (line.startsWith('data: ')) {
          const data = line.substring(6);
          if (data.trim() !== '[DONE]') {
            try {
              const parsed = JSON.parse(data);
              const content = parsed.choices?.[0]?.delta?.content;
              if (content) {
                processedTranscript += content;
              }
            } catch {
              // Ignore parsing errors for incomplete JSON chunks
            }
          }
        }
      });
    }

    return processedTranscript.trim();
  }

  /**
   * Batch/Full dictation - records complete audio then sends to backend for processing
   * This mirrors the mobile app's batch dictation approach using /dictations/process-audio
   */
  static async processBatchAudioDictation(
    audioBlob: Blob,
    options: {
      languageCode?: string;
      customVocabulary?: string;
      windowContext?: Record<string, any>;
      mode?: 'fast' | 'best' | 'answer';
      outputLanguage?: string;
    } = {},
  ): Promise<string> {
    const {
      languageCode,
      customVocabulary,
      windowContext,
      mode = 'fast',
      outputLanguage,
    } = options;

    try {
      logger.info('🎯 Starting batch dictation processing');
      const targetLanguage = await getCurrentOutputLanguageName();
      const systemLanguageCode = navigator.language.split('-')[0];

      const queryParams = new URLSearchParams({
        mode,
        language: languageCode || systemLanguageCode,
        targetLanguage: outputLanguage || targetLanguage,
        skipLLMProcessing: 'false',
      });

      if (customVocabulary) {
        queryParams.append('customVocabulary', customVocabulary);
      }
      if (windowContext) {
        queryParams.append('windowContext', JSON.stringify(windowContext));
      }

      const token = getToken();
      const response = await fetch(
        `${
          config.authBaseUrlV2
        }/dictations/process-audio?${queryParams.toString()}`,
        {
          method: 'POST',
          body: audioBlob,
          headers: {
            'Content-Type': audioBlob.type || 'audio/webm',
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to process batch dictation: ${response.status} - ${errorText}`,
        );
      }

      if (!response.body) {
        throw new Error('Response body is empty');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let processedTranscript = '';

      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        // eslint-disable-next-line no-loop-func
        lines.forEach((line) => {
          if (line.startsWith('data: ')) {
            const data = line.substring(6);
            if (data.trim() !== '[DONE]') {
              try {
                const parsed = JSON.parse(data);
                const content = parsed.choices?.[0]?.delta?.content;
                if (content) {
                  processedTranscript += content;
                }
              } catch {
                // Ignore parsing errors for incomplete JSON chunks
              }
            }
          }
        });
      }

      // Final filtering
      const trimmedProcessed = processedTranscript.trim().toLowerCase();
      if (
        processedTranscript.length === 0 ||
        processedTranscript.length < this.MIN_TRANSCRIPT_LENGTH ||
        this.ARTIFACTS.includes(trimmedProcessed)
      ) {
        logger.warn('⚠️ Processed transcript too short or contains artifacts');
        return '';
      }

      return this.removeMDashes(processedTranscript);
    } catch (error) {
      logger.error('❌ Error in batch dictation processing:', error);
      throw error;
    }
  }

  /**
   * Complete dictation flow: record -> transcribe -> process (LEGACY - kept for compatibility)
   */
  static async processAudioDictation(
    audioBlob: Blob,
    dispatch: AppDispatch,
    options: {
      fileName?: string;
      languageCode?: string;
      skipLLMProcessing?: boolean;
      customVocabulary?: string;
      windowContext?: Record<string, any>;
      mode?: 'fast' | 'best' | 'answer';
      outputLanguage?: string;
    } = {},
  ): Promise<string> {
    const {
      languageCode,
      skipLLMProcessing = false,
      customVocabulary,
      windowContext,
      mode = 'fast',
      outputLanguage,
    } = options;

    try {
      await dispatch(setAudioBitrate(LOW_QUALITY_AUDIO_BITRATE));
      console.info('🎯 Starting complete dictation processing');
      const targetLanguage = await getCurrentOutputLanguageName();
      const systemLanguageCode = navigator.language.split('-')[0];

      const queryParams = new URLSearchParams({
        mode,
        language: languageCode || systemLanguageCode,
        targetLanguage: outputLanguage || targetLanguage,
        skipLLMProcessing: String(skipLLMProcessing),
      });

      if (customVocabulary) {
        queryParams.append('customVocabulary', customVocabulary);
      }
      if (windowContext) {
        queryParams.append('windowContext', JSON.stringify(windowContext));
      }

      const token = getToken();
      const response = await fetch(
        `${
          config.authBaseUrlV2
        }/dictations/process-audio?${queryParams.toString()}`,
        {
          method: 'POST',
          body: audioBlob,
          headers: {
            'Content-Type': 'audio/webm',
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to process audio dictation: ${response.status} - ${errorText}`,
        );
      }

      if (!response.body) {
        throw new Error('Response body is empty');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let processedTranscript = '';

      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        // eslint-disable-next-line no-loop-func
        lines.forEach((line) => {
          if (line.startsWith('data: ')) {
            const data = line.substring(6);
            if (data.trim() !== '[DONE]') {
              try {
                const parsed = JSON.parse(data);
                const content = parsed.choices?.[0]?.delta?.content;
                if (content) {
                  processedTranscript += content;
                }
              } catch {
                // Ignore parsing errors for incomplete JSON chunks
              }
            }
          }
        });
      }

      // Final filtering
      const trimmedProcessed = processedTranscript.trim().toLowerCase();
      if (
        processedTranscript.length === 0 ||
        processedTranscript.length < this.MIN_TRANSCRIPT_LENGTH ||
        this.ARTIFACTS.includes(trimmedProcessed)
      ) {
        console.warn('⚠️ Processed transcript too short or contains artifacts');
        return '';
      }

      return this.removeMDashes(processedTranscript);
    } catch (error) {
      console.error('❌ Error in complete dictation processing:', error);
      throw error;
    } finally {
      dispatch(setAudioBitrate(HIGH_QUALITY_AUDIO_BITRATE));
    }
  }

  /**
   * Remove m-dashes and replace with regular dashes
   */
  private static removeMDashes(text: string): string {
    if (!text) return text;

    return text
      .replace(/—/g, '-') // em dash
      .replace(/–/g, '-') // en dash
      .replace(/―/g, '-'); // horizontal bar
  }

  /**
   * Build prompt for Answer Mode - responds to questions/commands like Siri
   */
  private static buildAnswerModePrompt(
    rawTranscript: string,
    targetLanguage: string,
  ): string {
    return `LANGUAGE INSTRUCTION: Your response must be entirely in ${targetLanguage}. Do not use English unless the target language IS English.

Answer this question concisely in ${targetLanguage}: ${rawTranscript}

FINAL REMINDER: Your entire response must be in ${targetLanguage}.`;
  }

  /**
   * Generate context prompt based on window information
   */
  private static generateContextPrompt(): string {
    // This would be implemented based on your specific context needs
    return '';
  }

  /**
   * Copy text to clipboard
   */
  static async copyToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      console.info('📋 Copied to clipboard');
    } catch (error) {
      console.error('❌ Failed to copy to clipboard:', error);
      throw error;
    }
  }

  /**
   * Get all dictations
   */
  static async getDictations(): Promise<DictationItem[]> {
    try {
      const response = await apiV2.get('/dictations');
      return response.data.data;
    } catch (error) {
      console.error('❌ Error fetching dictations:', error);
      throw error;
    }
  }

  /**
   * Get dictations by owner ID
   */
  static async getDictationsByOwner(ownerId: string): Promise<DictationItem[]> {
    try {
      const response = await apiV2.get(`/dictations/owner/${ownerId}`);
      return response.data.data;
    } catch (error) {
      console.error('❌ Error fetching dictations by owner:', error);
      throw error;
    }
  }

  /**
   * Get a specific dictation by ID
   */
  static async getDictation(id: string): Promise<DictationItem> {
    try {
      const response = await apiV2.get(`/dictations/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('❌ Error fetching dictation:', error);
      throw error;
    }
  }

  /**
   * Create a new dictation
   */
  static async createDictation(
    payload: CreateDictationPayload,
  ): Promise<DictationItem> {
    try {
      const response = await apiV2.post('/dictations', payload);
      return response.data.data;
    } catch (error) {
      console.error('❌ Error creating dictation:', error);
      throw error;
    }
  }

  /**
   * Update a dictation
   */
  static async updateDictation(
    id: string,
    payload: UpdateDictationPayload,
  ): Promise<DictationItem> {
    try {
      const response = await apiV2.put(`/dictations/${id}`, payload);
      return response.data.data;
    } catch (error) {
      console.error('❌ Error updating dictation:', error);
      throw error;
    }
  }

  /**
   * Delete a dictation
   */
  static async deleteDictation(id: string): Promise<void> {
    try {
      await apiV2.delete(`/dictations/${id}`);
      console.info('✅ Dictation deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting dictation:', error);
      throw error;
    }
  }
}

export default DictationService;
export type { DictationItem };
